import {
  type EFeatureFlag,
  EPropertyType,
  type IProperty,
  type IPropertyFilter,
} from '@mahawi/eshop-common/dist/src/types';

import {
  type EStockFilter,
  PUBLIC_FEATURE_FLAGS,
  STOCK_FILTER_DEFAULT,
  STOCK_FILTER_OPTIONS,
} from '../reducers/products/types';

export const PROPERTY_FILTER_PARAM_PREFIX = 'f_';
export const BRAND_PARAM = 'brand';
export const STOCK_PARAM = 'stock';
export const FEATURE_FLAGS_PARAM = 'flags';

export function applyFiltersToSearchParams(
  params: URLSearchParams,
  filters: IPropertyFilter[],
): URLSearchParams {
  const next = new URLSearchParams(params);

  for (const key of Array.from(next.keys())) {
    if (key.startsWith(PROPERTY_FILTER_PARAM_PREFIX)) {
      next.delete(key);
    }
  }

  for (const filter of filters) {
    const key = `${PROPERTY_FILTER_PARAM_PREFIX}${filter.code}`;

    switch (filter.type) {
      case EPropertyType.BOOLEAN:
        if (filter.booleanValue) {
          next.set(key, '1');
        }
        break;
      case EPropertyType.ENUM:
        if (filter.valuesCodes && filter.valuesCodes.length > 0) {
          next.set(key, filter.valuesCodes.join(','));
        }
        break;
      case EPropertyType.NUMBER:
        if (filter.min !== undefined || filter.max !== undefined) {
          next.set(key, `${filter.min ?? ''}..${filter.max ?? ''}`);
        }
        break;
      default:
        break;
    }
  }

  return next;
}

export function applyBrandToSearchParams(
  params: URLSearchParams,
  brandUUID: string | undefined,
): URLSearchParams {
  const next = new URLSearchParams(params);

  if (brandUUID) {
    next.set(BRAND_PARAM, brandUUID);
  } else {
    next.delete(BRAND_PARAM);
  }

  return next;
}

export function applyStockToSearchParams(
  params: URLSearchParams,
  stockFilter: EStockFilter | undefined,
): URLSearchParams {
  const next = new URLSearchParams(params);

  if (stockFilter && stockFilter !== STOCK_FILTER_DEFAULT) {
    next.set(STOCK_PARAM, stockFilter);
  } else {
    next.delete(STOCK_PARAM);
  }

  return next;
}

export function parseStockFromSearchParams(
  params: URLSearchParams,
): EStockFilter {
  const raw = params.get(STOCK_PARAM);

  return raw !== STOCK_FILTER_DEFAULT &&
    (STOCK_FILTER_OPTIONS as readonly string[]).includes(raw ?? '')
    ? (raw as EStockFilter)
    : STOCK_FILTER_DEFAULT;
}

export function applyFeatureFlagsToSearchParams(
  params: URLSearchParams,
  featureFlags: EFeatureFlag[],
): URLSearchParams {
  const next = new URLSearchParams(params);

  if (featureFlags.length > 0) {
    next.set(FEATURE_FLAGS_PARAM, featureFlags.join(','));
  } else {
    next.delete(FEATURE_FLAGS_PARAM);
  }

  return next;
}

export function parseFeatureFlagsFromSearchParams(
  params: URLSearchParams,
): EFeatureFlag[] {
  const raw = params.get(FEATURE_FLAGS_PARAM);

  if (!raw) {
    return [];
  }

  const codes = raw.split(',').filter((v: string): boolean => v.length > 0);

  return PUBLIC_FEATURE_FLAGS.filter((flag: EFeatureFlag): boolean =>
    codes.includes(flag),
  );
}

export function parseFiltersFromSearchParams(
  params: URLSearchParams,
  properties: IProperty[],
): IPropertyFilter[] {
  const filters: IPropertyFilter[] = [];

  for (const property of properties) {
    const raw = params.get(`${PROPERTY_FILTER_PARAM_PREFIX}${property.code}`);

    if (raw === null) {
      continue;
    }

    switch (property.type) {
      case EPropertyType.BOOLEAN:
        if (raw === '1') {
          filters.push({
            code: property.code,
            type: EPropertyType.BOOLEAN,
            booleanValue: true,
          });
        }
        break;
      case EPropertyType.ENUM: {
        const valuesCodes = raw
          .split(',')
          .filter((v: string): boolean => v.length > 0);
        if (valuesCodes.length > 0) {
          filters.push({
            code: property.code,
            type: EPropertyType.ENUM,
            valuesCodes,
          });
        }
        break;
      }
      case EPropertyType.NUMBER: {
        const [minRaw, maxRaw] = raw.split('..');
        const min = minRaw ? Number(minRaw) : undefined;
        const max = maxRaw ? Number(maxRaw) : undefined;
        const validMin =
          min !== undefined && !Number.isNaN(min) ? min : undefined;
        const validMax =
          max !== undefined && !Number.isNaN(max) ? max : undefined;

        if (validMin !== undefined || validMax !== undefined) {
          filters.push({
            code: property.code,
            type: EPropertyType.NUMBER,
            min: validMin,
            max: validMax,
          });
        }
        break;
      }
      default:
        break;
    }
  }

  return filters;
}
